import { useState, useMemo, useEffect } from 'react';
import type { Category } from '../types/category';
import '../../styles/adjustments.css';

const ADJUSTMENTS_STORAGE_KEY = 'planner_adjustments_detail';

export interface Adjustment {
  id: string;
  date: string;
  categoryId: number | null;
  amount: number;
  sign: '+' | '-'; // Default to '-'
  note?: string; // Optional note field
}

type SortColumn = 'date' | 'category' | 'amount';
type SortDirection = 'asc' | 'desc';

interface AdjustmentsProps {
  categories: Category[];
  onClose?: () => void;
}

export function Adjustments({ categories }: AdjustmentsProps) {
  const [adjustments, setAdjustments] = useState<Adjustment[]>(() => {
    const stored = localStorage.getItem(ADJUSTMENTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Migrate old adjustments without sign field
      return parsed.map((adj: any): Adjustment => ({
        ...adj,
        sign: (adj.sign === '+' ? '+' : '-') as '+' | '-',
      }));
    }
    return [];
  });

  // Get all categories (including groups) for the dropdown
  const allCategories = useMemo(() => {
    return categories.sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  // Initialize category names for existing rows
  useEffect(() => {
    if (allCategories.length > 0 && adjustments.length > 0) {
      const names: Record<string, string> = {};
      adjustments.forEach(adj => {
        if (adj.categoryId) {
          const category = allCategories.find(c => c.id === adj.categoryId);
          if (category) {
            names[adj.id] = category.name;
          }
        }
      });
      setRowCategoryNames(prev => ({ ...prev, ...names }));
    }
  }, [allCategories, adjustments.length]);

  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Form state for new row entry
  const [formDate, setFormDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [formCategoryId, setFormCategoryId] = useState<number | null>(null);
  const [formCategoryName, setFormCategoryName] = useState<string>('');
  const [formAmount, setFormAmount] = useState<number>(0);
  const [formSign, setFormSign] = useState<'+' | '-'>('-');
  const [formNote, setFormNote] = useState<string>('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  // Track which row's dropdown is open (by adjustment id)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // Track category names for each row (for combobox display)
  const [rowCategoryNames, setRowCategoryNames] = useState<Record<string, string>>({});

  // Filter categories based on input (for form or row)
  useEffect(() => {
    let filterValue = '';
    
    if (openDropdownId) {
      // Filtering for a table row
      filterValue = rowCategoryNames[openDropdownId] || '';
    } else if (showCategoryDropdown) {
      // Filtering for the form
      filterValue = formCategoryName;
    }
    
    if (!filterValue.trim()) {
      setFilteredCategories(allCategories);
    } else {
      const filter = filterValue.toLowerCase();
      setFilteredCategories(
        allCategories.filter(cat => 
          cat.name.toLowerCase().includes(filter)
        )
      );
    }
  }, [formCategoryName, showCategoryDropdown, openDropdownId, rowCategoryNames, allCategories]);

  // Initialize filtered categories when allCategories changes
  useEffect(() => {
    if (allCategories.length > 0 && !formCategoryName.trim()) {
      setFilteredCategories(allCategories);
    }
  }, [allCategories]);


  // Sort adjustments by selected column
  const sortedAdjustments = useMemo(() => {
    return [...adjustments].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'date': {
          // Dates are stored in YYYY-MM-DD format
          // Convert to Date objects for proper chronological sorting
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          // Compare as numbers (timestamps) for accurate sorting
          comparison = dateA - dateB;
          break;
        }
        case 'category': {
          const categoryA = allCategories.find(c => c.id === a.categoryId);
          const categoryB = allCategories.find(c => c.id === b.categoryId);
          const nameA = categoryA?.name || '';
          const nameB = categoryB?.name || '';
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'amount': {
          const amountA = (a.sign === '+' ? 1 : -1) * a.amount;
          const amountB = (b.sign === '+' ? 1 : -1) * b.amount;
          comparison = amountA - amountB;
          break;
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [adjustments, sortColumn, sortDirection, allCategories]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default direction
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const handleAddRow = () => {
    // Create new adjustment from form data
    const newAdjustment: Adjustment = {
      id: Date.now().toString(),
      date: formDate,
      categoryId: formCategoryId,
      amount: Math.abs(formAmount || 0), // Always store as positive
      sign: formSign,
      note: formNote.trim() || undefined,
    };
    // Add new row at the beginning so it appears at top
    const newAdjustments = [newAdjustment, ...adjustments];
    saveAdjustments(newAdjustments);
    
    // Reset form to defaults
    const today = new Date().toISOString().split('T')[0];
    setFormDate(today);
    setFormCategoryId(null);
    setFormCategoryName('');
    setFormAmount(0);
    setFormSign('-');
    setFormNote('');
    setShowCategoryDropdown(false);
  };

  const handleCategoryInputChange = (value: string) => {
    setFormCategoryName(value);
    setShowCategoryDropdown(true);
    // Try to find matching category
    const match = allCategories.find(cat => 
      cat.name.toLowerCase() === value.toLowerCase()
    );
    if (match) {
      setFormCategoryId(match.id);
    } else {
      setFormCategoryId(null);
    }
  };

  const handleCategorySelect = (category: Category) => {
    setFormCategoryName(category.name);
    setFormCategoryId(category.id);
    setShowCategoryDropdown(false);
  };

  // Get category name for a row
  const getCategoryName = (categoryId: number | null): string => {
    if (!categoryId) return '';
    const category = allCategories.find(c => c.id === categoryId);
    return category?.name || '';
  };

  // Handle category input change for a row
  const handleRowCategoryInputChange = (id: string, value: string) => {
    setRowCategoryNames(prev => ({ ...prev, [id]: value }));
    setOpenDropdownId(id);
    // Try to find matching category
    const match = allCategories.find(cat => 
      cat.name.toLowerCase() === value.toLowerCase()
    );
    if (match) {
      handleCategoryChange(id, match.id);
    } else {
      handleCategoryChange(id, null);
    }
  };

  // Handle category selection for a row
  const handleRowCategorySelect = (id: string, category: Category) => {
    setRowCategoryNames(prev => ({ ...prev, [id]: category.name }));
    handleCategoryChange(id, category.id);
    setOpenDropdownId(null);
  };

  const handleFormSignToggle = () => {
    setFormSign(formSign === '+' ? '-' : '+');
  };

  const handleFormAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Toggle sign with Space, +, or - keys
    if (e.key === ' ' || e.key === '+' || e.key === '-') {
      e.preventDefault();
      handleFormSignToggle();
    }
  };

  const handleDeleteRow = (id: string) => {
    const newAdjustments = adjustments.filter(adj => adj.id !== id);
    saveAdjustments(newAdjustments);
  };

  const saveAdjustments = (newAdjustments: Adjustment[]) => {
    setAdjustments(newAdjustments);
    localStorage.setItem(ADJUSTMENTS_STORAGE_KEY, JSON.stringify(newAdjustments));
    // Trigger a custom event to notify App.tsx that adjustments changed
    window.dispatchEvent(new CustomEvent('adjustmentsChanged'));
  };

  const handleDateChange = (id: string, date: string) => {
    const newAdjustments = adjustments.map(adj =>
      adj.id === id ? { ...adj, date } : adj
    );
    saveAdjustments(newAdjustments);
  };

  const handleCategoryChange = (id: string, categoryId: number | null) => {
    const newAdjustments = adjustments.map(adj =>
      adj.id === id ? { ...adj, categoryId } : adj
    );
    saveAdjustments(newAdjustments);
    // Update category name in state
    if (categoryId) {
      const category = allCategories.find(c => c.id === categoryId);
      if (category) {
        setRowCategoryNames(prev => ({ ...prev, [id]: category.name }));
      }
    } else {
      setRowCategoryNames(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  const handleAmountChange = (id: string, amount: number) => {
    // Always store amount as positive - the sign toggle determines if it's added or subtracted
    const positiveAmount = Math.abs(amount || 0);
    const newAdjustments = adjustments.map(adj =>
      adj.id === id ? { ...adj, amount: positiveAmount } : adj
    );
    saveAdjustments(newAdjustments);
  };

  const handleNoteChange = (id: string, note: string) => {
    const newAdjustments = adjustments.map(adj =>
      adj.id === id ? { ...adj, note: note.trim() || undefined } : adj
    );
    saveAdjustments(newAdjustments);
  };

  const handleSignToggle = (id: string) => {
    const newAdjustments = adjustments.map(adj =>
      adj.id === id ? { ...adj, sign: adj.sign === '+' ? '-' : '+' } as Adjustment : adj
    );
    saveAdjustments(newAdjustments);
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, id: string) => {
    // Toggle sign with Space, +, or - keys
    if (e.key === ' ' || e.key === '+' || e.key === '-') {
      e.preventDefault();
      handleSignToggle(id);
    }
  };

  return (
    <div className="adjustments-container">
      <div className="adjustments-header">
        <h2>Adjustments</h2>
      </div>
      
      <div className="adjustments-table-wrapper">
        {/* Form row above table - styled to look like a table row */}
        <table className="adjustments-form-table">
          <tbody>
            <tr className="adjustments-form-row">
              <td>
                <label className="new-entry-label">New entry:</label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="date-input date-input-yyyymmdd"
                  data-date={formDate}
                />
              </td>
              <td>
                <div className="category-combobox-wrapper">
                  <input
                    type="text"
                    value={formCategoryName}
                    onChange={(e) => handleCategoryInputChange(e.target.value)}
                    onFocus={() => setShowCategoryDropdown(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => setShowCategoryDropdown(false), 200);
                    }}
                    className="category-combobox-input"
                    placeholder="Select or type category name"
                  />
                  {showCategoryDropdown && filteredCategories.length > 0 && (
                    <div className="category-combobox-dropdown">
                      {filteredCategories.map((category) => (
                        <div
                          key={category.id}
                          className="category-combobox-option"
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent blur
                            handleCategorySelect(category);
                          }}
                        >
                          {category.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div className="amount-input-wrapper">
                  <button
                    type="button"
                    onClick={handleFormSignToggle}
                    className={`sign-toggle ${formSign === '+' ? 'positive' : 'negative'}`}
                    title="Toggle sign (Space, +, or -)"
                    aria-label={`Sign: ${formSign}`}
                  >
                    {formSign}
                  </button>
                  <span className="currency-symbol">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formAmount || ''}
                    onChange={(e) => setFormAmount(parseFloat(e.target.value) || 0)}
                    onKeyDown={handleFormAmountKeyDown}
                    className="amount-input"
                    placeholder="0.00"
                  />
                </div>
              </td>
              <td>
                <input
                  type="text"
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  className="note-input"
                  placeholder="Optional note"
                />
              </td>
              <td>
                <button onClick={handleAddRow} className="add-row-button">
                  + Add Row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <table className="adjustments-table">
          <thead>
            <tr>
              <th 
                className="sortable-header"
                onClick={() => handleSort('date')}
                title="Sort by date"
              >
                Date
                {sortColumn === 'date' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('category')}
                title="Sort by category"
              >
                Category
                {sortColumn === 'category' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('amount')}
                title="Sort by amount"
              >
                Amount
                {sortColumn === 'amount' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAdjustments.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-message">
                  No adjustments yet. Click "+ Add Row" to add one.
                </td>
              </tr>
            ) : (
              sortedAdjustments.map((adjustment) => (
                <tr key={adjustment.id}>
                  <td>
                    <input
                      type="date"
                      value={adjustment.date}
                      onChange={(e) => handleDateChange(adjustment.id, e.target.value)}
                      className="date-input date-input-yyyymmdd"
                      data-date={adjustment.date}
                    />
                  </td>
                  <td>
                    <div className="category-combobox-wrapper">
                      <input
                        type="text"
                        value={rowCategoryNames[adjustment.id] || getCategoryName(adjustment.categoryId)}
                        onChange={(e) => handleRowCategoryInputChange(adjustment.id, e.target.value)}
                        onFocus={() => setOpenDropdownId(adjustment.id)}
                        onBlur={() => {
                          // Delay to allow click on dropdown item
                          setTimeout(() => setOpenDropdownId(null), 200);
                        }}
                        className="category-combobox-input"
                        placeholder="Select or type category name"
                      />
                      {openDropdownId === adjustment.id && filteredCategories.length > 0 && (
                        <div className="category-combobox-dropdown">
                          {filteredCategories.map((category) => (
                            <div
                              key={category.id}
                              className="category-combobox-option"
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent blur
                                handleRowCategorySelect(adjustment.id, category);
                              }}
                            >
                              {category.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="amount-input-wrapper">
                      <button
                        type="button"
                        onClick={() => handleSignToggle(adjustment.id)}
                        className={`sign-toggle ${adjustment.sign === '+' ? 'positive' : 'negative'}`}
                        title="Toggle sign (Space, +, or -)"
                        aria-label={`Sign: ${adjustment.sign}`}
                      >
                        {adjustment.sign}
                      </button>
                      <span className="currency-symbol">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={adjustment.amount || ''}
                        onChange={(e) => handleAmountChange(adjustment.id, parseFloat(e.target.value) || 0)}
                        onKeyDown={(e) => handleAmountKeyDown(e, adjustment.id)}
                        className="amount-input"
                        placeholder="0.00"
                      />
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={adjustment.note || ''}
                      onChange={(e) => handleNoteChange(adjustment.id, e.target.value)}
                      className="note-input"
                      placeholder="Optional note"
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteRow(adjustment.id)}
                      className="delete-button"
                      aria-label="Delete row"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
